import {customElement, property, queryAll} from "lit/decorators.js";
import {html, type TemplateResult} from "lit";
import {FormInputElement} from "./formInputElement";
import {elementFrom} from "../../../common/utils/html";
import type {DirectiveResult} from "lit/directive";
import type {Maybe} from "../../../bootstrap/common/types";

export type DataRadioButton = {
    value: string;
    label: string;
    preset: boolean;
};

const RADIO_CIRCLE_INLINE_SVG = `
    <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
        <circle class="outer-circle" cx="16" cy="16" r="15.238" fill="currentColor" stroke-linecap="round" stroke-width="1.5238"/>
        <circle class="inner-circle" cx="16" cy="16" r="7.2727" fill="currentColor" stroke="#fff" stroke-width="1.4546"/>
    </svg>
`;

@customElement("eop-form-radio-buttons")
export class EopFormRadioButtons extends FormInputElement<string> {

    @property({attribute: "label"})
    public label: string;
    @property({attribute: "options", type: Array})
    public options: DataRadioButton[] = [];
    @queryAll("input")
    private inputs: NodeListOf<HTMLInputElement>;
    private iconNodes = new Map<number, Element>();

    public constructor() {
        super("");
    }

    public render(): TemplateResult {
        return html`
            <div class="input-element ${this.basicClassMap()}" ?required=${this.required}>
                <label class="label">
                    <span class="label-text">${this.label}</span>
                    ${this.labelSuffix()}
                </label>
                <div class="radio-buttons">
                    ${this.renderRadioButtons()}
                </div>
            </div>
            ${this.renderValidationMessages()}
        `;
    }

    public focusInput(): void {
        this.inputs.item(0)?.focus();
    }

    private renderRadioButtons(): DirectiveResult[] {
        return this.options.map((option, index) => {
            return html`
                <div class="radio-button">
                    <label>
                        <input type="radio"
                               .value=${option.value}
                               ?checked=${option.value === this.value}
                               ?required=${this.required}
                               @input=${this.select}
                               name=${this.getFullId()}
                               id="${this.getFullId()}-${index}"
                        >
                        <span class="icon">${this.iconNode(index)}</span>
                        <span class="radio-label">${option.label}</span>
                    </label>
                </div>
            `;
        });
    }

    protected preset(): Maybe<string> {
        return this.options
            .find(option => option.preset)
            ?.value;
    }

    protected valueEnhancements(inputValue: string): Map<string, string> {
        return new Map([["label", this.descriptorForValue(inputValue)]]);
    }

    private iconNode(index: number): Element {
        if (!this.iconNodes.has(index)) {
            this.iconNodes.set(index, elementFrom(RADIO_CIRCLE_INLINE_SVG));
        }
        return this.iconNodes.get(index)!;
    }

    private select(event: Event): void {
        const value = (event.currentTarget as HTMLInputElement).value;
        this.updateValue(value);
    }

    private descriptorForValue(value: string): string {
        return this.options
                .find(option => value === option.value)
                ?.label
            ?? "";
    }

}